/**
 * "Working appeals" — the day-to-day workflow category.
 */

import type { HelpArticle } from '../types';

export const appealsArticles: HelpArticle[] = [
  {
    slug: 'the-appeals-queue',
    title: 'The Appeals queue — your daily work list',
    summary: 'Every live appeal, grouped so you can see what needs doing next.',
    category: 'appeals',
    keywords: ['appeals', 'queue', 'work list', 'pipeline', 'my work', 'todo', 'backlog', 'filter'],
    related: ['what-is-an-appeal', 'moving-an-appeal-forward', 'deadlines-and-hearings'],
    body: [
      {
        kind: 'text',
        text: 'Click Appeals in the Workqueue section. This is the screen most people live in during the season.',
      },
      {
        kind: 'text',
        text: 'Appeals are grouped by which stage they are at, so the queue reads as a set of piles: things not yet filed, things waiting on the assessor, things with a hearing coming up, things resolved but not yet billed.',
      },
      {
        kind: 'shot',
        src: '/help-shots/appeals-queue.png',
        alt: 'The Appeals queue screen showing appeals grouped into stages, each row listing the parcel, the client, the deadline and the assigned person.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Work the piles right to left',
        text: 'Finishing something that is nearly done is worth more than starting something new. Appeals sitting in "resolved but not invoiced" are money you have already earned.',
      },
      {
        kind: 'heading',
        text: 'Narrowing the list',
      },
      {
        kind: 'bullets',
        items: [
          'Filter by assigned person to see only your own.',
          'Filter by client when a client calls and asks where things stand.',
          'Filter by jurisdiction when you are preparing for one office’s hearing day.',
          'Sort by deadline when you want to know what is about to bite.',
        ],
      },
      {
        kind: 'text',
        text: 'If you build a filter you use often, save it. See the article on saved views.',
      },
    ],
  },

  {
    slug: 'moving-an-appeal-forward',
    title: 'Moving an appeal to its next stage',
    summary: 'How to record that something happened — a protest was filed, a hearing was held, a decision arrived.',
    category: 'appeals',
    keywords: ['advance', 'progress', 'update appeal', 'change stage', 'next step', 'record', 'settle', 'withdraw', 'outcome'],
    related: ['what-is-an-appeal', 'the-appeals-queue', 'what-is-a-reduction'],
    body: [
      {
        kind: 'text',
        text: 'An appeal moves forward when you tell the app something happened in the real world. It does not move on its own.',
      },
      {
        kind: 'steps',
        steps: [
          'Open the parcel, then its Appeals tab. Or click straight through from the Appeals queue.',
          'Click the button to advance the appeal.',
          'Pick what happened, and enter the date it happened on — not today’s date, unless those are the same.',
          'Fill in whatever the new stage needs. Filing a protest asks for the filing date. Recording a resolution asks for the agreed value.',
          'Save.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Dates are evidence',
        text: 'Enter the real date something happened. Deadline calculations, and any later argument about whether you filed in time, are built on these dates. Back-dating to today because it is quicker will eventually cost somebody a year of tax.',
      },
      {
        kind: 'heading',
        text: 'Recording the result',
      },
      {
        kind: 'text',
        text: 'When an appeal settles, you enter the agreed value. The app compares that to the assessor’s opening number, works out the reduction, applies the tax rates for each jurisdiction, and produces the saving. From that it can calculate your fee.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Withdrawing is a legitimate outcome',
        text: 'If you look at the evidence and conclude the assessor’s number is fair, withdraw the appeal. That is a real answer and it closes the file cleanly. It is much better than leaving it open forever.',
      },
    ],
  },

  {
    slug: 'deadlines-and-hearings',
    title: 'Deadlines and hearings',
    summary: 'The dates that cannot slip, and how the app warns you about them.',
    category: 'appeals',
    keywords: ['deadline', 'hearing', 'due date', 'protest deadline', 'filing deadline', 'arb hearing', 'schedule', 'miss', 'late', 'reminder'],
    related: ['the-calendar', 'what-is-an-appeal', 'notifications'],
    body: [
      {
        kind: 'text',
        text: 'Property tax runs on hard deadlines set by law. They are not negotiable and they are not extended because you were busy.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Protest deadline', meaning: 'The last day to formally disagree with this year’s value. This is the one that matters most. Miss it and the client pays the full amount for the whole year.' },
          { term: 'Hearing date', meaning: 'The day your case is heard. Showing up unprepared and not showing up cost about the same.' },
          { term: 'Litigation window', meaning: 'A short period after a decision in which you could still go to court. Once it shuts, it is shut.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Deadlines differ by state and sometimes by county',
        text: 'Do not assume the date you know from one jurisdiction applies to another. The app holds the rules per state and per assessor, which is why it can warn you — but only if the parcel is attached to the right assessor.',
      },
      {
        kind: 'heading',
        text: 'How you get warned',
      },
      {
        kind: 'bullets',
        items: [
          'Upcoming events on your dashboard, with deadlines marked in red.',
          'The Calendar screen, which shows everything laid out by date.',
          'Notifications through the bell in the top bar.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'A confidence marker on hearing dates',
        text: 'Some hearing dates are confirmed in writing and some are a best guess from a phone call. The app tracks how sure the date is. A low-confidence date is a prompt to call and confirm, not a reason to relax.',
      },
    ],
  },

  {
    slug: 'the-calendar',
    title: 'The Calendar',
    summary: 'Everything with a date on it, in one place — and how to get it into your own calendar app.',
    category: 'appeals',
    keywords: ['calendar', 'diary', 'schedule', 'dates', 'events', 'subscribe', 'ical', 'outlook', 'google calendar', 'sync'],
    related: ['deadlines-and-hearings', 'your-dashboard'],
    body: [
      {
        kind: 'text',
        text: 'Click Calendar in the Workqueue section. It shows deadlines, hearings and reminders across all your work.',
      },
      {
        kind: 'shot',
        src: '/help-shots/calendar.png',
        alt: 'The calendar screen showing a month grid with colored entries for appeal deadlines, hearings and reminders on various days.',
      },
      {
        kind: 'text',
        text: 'Entries are color-coded by what they are. Click one to jump to the parcel or appeal it belongs to.',
      },
      {
        kind: 'heading',
        text: 'Filtering',
      },
      {
        kind: 'text',
        text: 'There is a "Deadlines only" filter for when you want the legal dates without the reminders and meetings cluttering the view.',
      },
      {
        kind: 'heading',
        text: 'Seeing it in your own calendar',
      },
      {
        kind: 'text',
        text: 'You can subscribe to your AppealStack calendar from Outlook, Google Calendar or Apple Calendar, so deadlines appear alongside your meetings. Look for the subscribe option on the Calendar screen — it gives you a private web address to paste into your calendar app.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'That address is a key — do not share it',
        text: 'Anyone with the link can read your calendar without signing in. Do not put it in a shared document or email it around. If you think it has leaked, ask an admin to reset it.',
      },
    ],
  },

  {
    slug: 'notes-and-activity',
    title: 'Notes, and the record of who did what',
    summary: 'Leaving notes for your colleagues, and reading the history of a record.',
    category: 'appeals',
    keywords: ['notes', 'comment', 'activity', 'history', 'log', 'who changed', 'audit', 'mention', 'tag someone', 'follow'],
    related: ['the-appeals-queue', 'activity-and-audit-logs'],
    body: [
      {
        kind: 'text',
        text: 'Most records — clients, properties, parcels, appeals — have a Notes panel. Use it for anything a colleague would need to know if they picked this up tomorrow.',
      },
      {
        kind: 'bullets',
        intro: 'Good things to write down:',
        items: [
          'What the assessor said on the phone, and who said it.',
          'Why you chose the value you are arguing for.',
          'Anything the client asked you to do or not do.',
          'Why you withdrew, if you withdrew.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Mention a colleague to notify them',
        text: 'Type @ and their name in a note. They get a notification. This is much more reliable than telling them in passing.',
      },
      {
        kind: 'heading',
        text: 'Following a record',
      },
      {
        kind: 'text',
        text: 'The star button on a record means "tell me when this changes". Useful for a handful of high-value parcels you want to watch. Not useful on two hundred of them, because then every change is noise.',
      },
      {
        kind: 'heading',
        text: 'The activity feed',
      },
      {
        kind: 'text',
        text: 'Separately from notes, every record keeps an automatic history of changes — who changed what, and when. You do not write this and you cannot edit it. When somebody asks "who moved this value", that is where the answer is.',
      },
    ],
  },
];
